import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLanguage, useT } from "@/lib/LanguageContext";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
// Relative, not "@/…": real modules under Jest, no alias there.
import { apiErrorCode } from "../lib/apiErrors";
import LoadFailure from "../components/ui/LoadFailure";
import useReviewRequest from "../reviews/hooks/useReviewRequest";
import StarRating from "../reviews/components/StarRating";
import ReviewCard from "../reviews/components/ReviewCard";
import { reviewErrorText } from "../reviews/errorText";
import { fmtDateTime } from "../reviews/format";
import { REVIEW_PATHS, SUB_RATING_KEYS, TEXT_MAX_LEN, TOKEN_QUERY_PARAM } from "../reviews/contract";
import { REVIEWS } from "../constants/testIds/reviews";

/**
 * `/avis?token=…` — the client's review form, on the PRACTICE's own origin.
 *
 * WHY HERE AND NOT ON THE PLATFORM HOST. The link is mailed under the practitioner's brand,
 * and the client portal and the consult room already live on that origin; the platform host
 * would run every request with the tenant ContextVar set to the demo practice. The token is
 * the capability, the API is host-agnostic, and `/avis` is registered on both routers
 * exactly as `/gerer-rdv` is.
 *
 * EVERY ANSWER A LINK CAN GET IS A SCREEN, not an error. The server answers **200** for a
 * request that is already `submitted` or `expired` precisely so this page can explain which
 * — a 404 there would have made "you already reviewed this" indistinguishable from "this
 * link is nonsense". The only 404 is `REVIEW_TOKEN_UNKNOWN`, one uniform answer for absent,
 * malformed and unknown tokens (so the endpoint is not an oracle for which strings are
 * tokens), and it gets its own refusal panel rather than a retry button: retrying a link
 * that names nothing will never name anything.
 *
 * PRECEDENCE BETWEEN THE PANELS, stated rather than incidental:
 *   1. `submitted` — a review that EXISTS is the strongest fact on the page and outranks
 *      both a lapsed clock and a paused program; the client wrote it, they may read it back.
 *   2. `expired` — the 30 days are over, so there is nothing to submit whatever the program
 *      is doing.
 *   3. `program_enabled: false` — the operator paused the program; the form would be refused
 *      with `REVIEWS_DISABLED` on POST, so it is not offered.
 *   4. otherwise, the form.
 *
 * THE REFUSAL IS READ FROM THE `code`, NEVER FROM `message`. `detail.message` is English and
 * unlocalisable; `reviewErrorText` maps the closed set to a sentence we wrote and anything
 * else to "we cannot say what went wrong". A `REVIEW_ALREADY_SUBMITTED` on POST — six
 * concurrent taps, a replayed link, a reload of a request that has since been answered — is
 * not shown as a failure at all: the review exists, so the page flips to the submitted panel.
 */
const SUB_LEGEND_KEYS = {
  communication: "rev.form.sub.communication",
  expertise: "rev.form.sub.expertise",
  value: "rev.form.sub.value",
};

const GOLD_PILL =
  "inline-flex items-center justify-center px-5 h-11 rounded-full bg-sol-gold text-black "
  + "text-sm font-medium hover:bg-sol-gold/90 transition disabled:opacity-50 disabled:cursor-not-allowed";

export default function ReviewForm() {
  const t = useT("marketplace");
  const tv = useT("common");
  const { lang } = useLanguage();

  // Read once, from the URL the mail opened. `useSearchParams` would pull react-router-dom
  // into this module for one string; `ManageBooking` reads its own token the same way.
  const token = useMemo(
    () => new URLSearchParams(window.location.search).get(TOKEN_QUERY_PARAM) || "",
    [],
  );

  const { context, loading, loadError, reload, applySubmitted } = useReviewRequest(token);

  const [rating, setRating] = useState(0);
  const [subs, setSubs] = useState({});
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState("");

  // The server states its own cap on the context; the compile-time constant is the floor
  // for a payload from a build that predates it. The textarea and the counter read the same
  // number, so the counter can never promise room the `maxLength` will not give.
  const maxLen = Number.isInteger(context?.text_max_len) ? context.text_max_len : TEXT_MAX_LEN;
  const canSubmit = rating >= 1 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setRefusal("");
    try {
      const { data } = await api.post(REVIEW_PATHS.submit, {
        token,
        rating,
        // ALL THREE KEYS, ALWAYS, with `null` for a facet the client left alone — the
        // contract's shape, so "not answered" is a value rather than a missing field the
        // server would have to guess the meaning of.
        sub_ratings: Object.fromEntries(SUB_RATING_KEYS.map((k) => [k, subs[k] || null])),
        text: text.trim(),
      });
      applySubmitted(data);
      toast.success(t("rev.form.thanks"));
    } catch (err) {
      if (apiErrorCode(err) === "REVIEW_ALREADY_SUBMITTED") {
        // Not a failure: the review is there. The panel below says so, and the row itself
        // is unknown to this response — the server answered a refusal, not a review.
        applySubmitted(null);
        return;
      }
      const message = reviewErrorText(err, { t, tv, fallback: tv("errors.saveFailed") });
      setRefusal(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-20 max-w-2xl mx-auto px-6">
        {!token ? (
          <Panel testId={REVIEWS.formMissingToken} title={t("rev.form.missingTokenTitle")}>
            {t("rev.form.missingTokenBody")}
          </Panel>
        ) : loadError ? (
          apiErrorCode(loadError) === "REVIEW_TOKEN_UNKNOWN" ? (
            <Panel testId={REVIEWS.formRefusal} title={t("rev.form.refusalTitle")}>
              {t("rev.error.REVIEW_TOKEN_UNKNOWN")}
            </Panel>
          ) : (
            <LoadFailure
              message={reviewErrorText(loadError, { t, tv, fallback: tv("errors.loadFailed") })}
              onRetry={reload}
              retryLabel={tv("actions.retry")}
              testId={REVIEWS.formLoadError}
              retryTestId="review-form-retry"
            />
          )
        ) : loading || !context ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-eh-sage-deep" />
          </div>
        ) : context.state === "submitted" ? (
          <SubmittedPanel context={context} t={t} lang={lang} />
        ) : context.state === "expired" ? (
          <Panel testId={REVIEWS.formExpired} title={t("rev.form.expiredTitle")}>
            {t("rev.form.expiredBody")}
          </Panel>
        ) : !context.program_enabled ? (
          <Panel testId={REVIEWS.formDisabled} title={t("rev.form.disabledTitle")}>
            {t("rev.form.disabledBody")}
          </Panel>
        ) : (
          <form onSubmit={submit} data-testid={REVIEWS.form} className="space-y-8">
            <ContextCard context={context} t={t} lang={lang} />

            <StarRating
              value={rating}
              onChange={setRating}
              variant="light"
              size="lg"
              legend={t("rev.form.overallLegend")}
              starLabel={(n) => t("rev.form.starLabel", { n })}
              name="review-rating"
              starTestId={(n) => REVIEWS.formStar(n)}
              disabled={busy}
            />

            <div className="space-y-4">
              <p className="text-sm text-eh-muted">{t("rev.form.subHint")}</p>
              {SUB_RATING_KEYS.map((key) => (
                <StarRating
                  key={key}
                  value={subs[key] || 0}
                  onChange={(n) => setSubs((prev) => ({ ...prev, [key]: n }))}
                  variant="light"
                  size="md"
                  legend={t(SUB_LEGEND_KEYS[key])}
                  starLabel={(n) => t("rev.form.starLabel", { n })}
                  name={`review-sub-${key}`}
                  starTestId={(n) => REVIEWS.formSubStar(key, n)}
                  disabled={busy}
                />
              ))}
            </div>

            <div>
              <label htmlFor="review-text" className="block text-sm text-eh-ink mb-1.5">
                {t("rev.form.textLabel")}
              </label>
              <textarea
                id="review-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={maxLen}
                rows={6}
                disabled={busy}
                placeholder={t("rev.form.textPlaceholder")}
                data-testid={REVIEWS.formText}
                className="w-full rounded-2xl border border-eh-border-soft bg-white p-4 text-sm text-eh-ink focus:outline-none focus:border-eh-sage-deep"
              />
              <p
                className="text-xs text-eh-muted tabular-nums text-right mt-1"
                data-testid={REVIEWS.formTextCounter}
              >
                {t("rev.form.textCounter", { n: text.length, max: maxLen })}
              </p>
            </div>

            {refusal && (
              <p
                role="alert"
                data-testid={REVIEWS.formError}
                className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-2xl p-4"
              >
                {refusal}
              </p>
            )}

            <button type="submit" disabled={!canSubmit} data-testid={REVIEWS.formSubmit} className={GOLD_PILL}>
              {busy ? t("rev.form.submitting") : t("rev.form.submit")}
            </button>
          </form>
        )}
      </section>
    </ThemedPageLayout>
  );
}

/** The practice, the service and the appointment this review is about. */
function ContextCard({ context, t, lang }) {
  const p = context.practitioner || {};
  return (
    <div
      data-testid={REVIEWS.formContext}
      className="rounded-2xl border border-eh-border-soft bg-white p-5 flex items-start gap-4"
    >
      {p.logo_url ? (
        <img src={p.logo_url} alt={p.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center font-display flex-shrink-0"
          style={{
            background: p.primary_color ? `${p.primary_color}30` : "rgba(230,184,92,0.15)",
            color: p.primary_color || "#B27733",
          }}
        >
          {(p.name || "?").charAt(0).toUpperCase()}
        </div>
      )}
      <div className="min-w-0">
        <p className="font-medium text-eh-ink">{p.name}</p>
        {context.service_name && <p className="text-sm text-eh-muted mt-0.5">{context.service_name}</p>}
        <p className="text-xs text-eh-muted mt-0.5 tabular-nums">{fmtDateTime(context.slot_start, lang)}</p>
      </div>
    </div>
  );
}

/**
 * The thanks screen — reached both by submitting and by re-opening a spent link.
 *
 * `context.review` is the row when the server sent one and `null` when the page got here
 * from a `REVIEW_ALREADY_SUBMITTED` refusal, which carries no review. The card is therefore
 * conditional: the sentence is the fact, the card is a courtesy.
 *
 * The marketplace link is CROSS-ORIGIN (the platform host, built by the server — the tenant
 * origin cannot know the platform's) and carries NO referral parameter: a client walking
 * back to the storefront is not a referral, and stamping one would credit the practice for
 * bringing the platform a visitor the platform already had.
 */
function SubmittedPanel({ context, t, lang }) {
  const url = context.practitioner?.marketplace_url || "";
  return (
    <div
      data-testid={REVIEWS.formSubmitted}
      className="rounded-2xl border border-eh-border-soft bg-white p-6 space-y-4"
    >
      <h1 className="font-display text-2xl text-eh-ink">{t("rev.form.submittedTitle")}</h1>
      <p className="text-sm text-eh-muted">{t("rev.form.submittedBody")}</p>
      {context.review && (
        <ul className="m-0 p-0">
          <ReviewCard review={context.review} t={t} lang={lang} variant="light" />
        </ul>
      )}
      {url && (
        <p className="m-0">
          <a href={url} data-testid={REVIEWS.formMarketplaceLink} className={GOLD_PILL}>
            {t("rev.form.marketplaceLink")}
          </a>
        </p>
      )}
    </div>
  );
}

/** A one-sentence answer that is not an error to retry: no token, spent link, paused program. */
function Panel({ testId, title, children }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-eh-border-soft bg-white p-6 text-center">
      <h1 className="font-display text-2xl text-eh-ink mb-2">{title}</h1>
      <p className="text-sm text-eh-muted m-0">{children}</p>
    </div>
  );
}
